import { spawn } from 'node:child_process'
import { mkdir, writeFile } from 'node:fs/promises'
import { dirname, resolve } from 'node:path'
import { Command } from 'commander'
import { createApiClient, type ApiClient } from '../http.js'
import { files } from '../api/catalog.js'
import { call } from '../api/client.js'
import { resolveWorkspaceId } from '../context.js'
import { decodeJwtPayload } from '../jwt.js'
import { printJson } from '../output.js'
import { ENGINE_DEFAULT_PERMISSIONS } from '../api/scopes.js'
import { buildLocalContext, contextEnv, type LocalExecutionContext } from '../dev/context.js'
import { manifestPermissions, readManifestDocument } from '../dev/manifest.js'
import { findManifest } from '../vpkg/manifest.js'
import type { FileDto } from './files.js'

interface TokenFlags {
  workspace?: string
  scopes?: string[]
  manifest?: string
  boundary?: string
  branch: string
  json?: boolean
}

/** Resolve the scope set for a dev token: explicit flags → manifest permissions → engine default set. */
async function resolveDevScopes(flags: { scopes?: string[]; manifest?: string }): Promise<string[]> {
  if (flags.scopes?.length) return flags.scopes
  const manifestPath = flags.manifest ?? (await findManifest(process.cwd()).catch(() => undefined))
  if (manifestPath) {
    const manifest = await readManifestDocument(manifestPath)
    const permissions = manifestPermissions(manifest)
    if (permissions.length > 0) {
      console.error(`Using ${permissions.length} scope(s) from ${manifestPath}`)
      return permissions
    }
  }
  return [...ENGINE_DEFAULT_PERMISSIONS]
}

/** Mint a workspace-bound (optionally node-bounded) access token like a platform run receives. */
async function mintDevToken(api: ApiClient, workspaceId: string, scopes: string[], boundary?: string, branch = 'main'): Promise<string> {
  let resourceId: string | undefined
  if (boundary) {
    const node = await call<FileDto>(api, files.get, {
      params: { path: boundary.replace(/^\/+/, '').replace(/\/+$/, '') },
      workspaceId,
      query: { branch },
    })
    if (!node.nodeId) throw new Error(`Could not resolve a node id for boundary path '${boundary}'.`)
    resourceId = node.nodeId
  }
  return api.mintAccessToken({
    scopes,
    workspaceId,
    ...(resourceId ? { resourceId, resourceKind: 'Node' as const } : {}),
  })
}

interface ContextFlags extends TokenFlags {
  file?: string
  tool?: string
  mode?: string
  timeout?: string
  arg?: string[]
  env?: string[]
  params?: string
  out?: string
}

async function buildContextFromFlags(api: ApiClient, options: ContextFlags): Promise<LocalExecutionContext> {
  const workspaceId = (await resolveWorkspaceId(options))!
  const scopes = await resolveDevScopes(options)
  const token = await mintDevToken(api, workspaceId, scopes, options.boundary, options.branch)

  const env: Record<string, string> = {}
  for (const pair of options.env ?? []) {
    const idx = pair.indexOf('=')
    if (idx < 0) throw new Error(`Invalid --env '${pair}' (expected KEY=value).`)
    env[pair.slice(0, idx)] = pair.slice(idx + 1)
  }

  let fileMimeType: string | undefined
  if (options.file) {
    // Best-effort metadata for the target file; keep the context usable when it doesn't exist yet.
    const remote = await call<FileDto>(api, files.get, {
      params: { path: options.file.replace(/^\/+/, '') },
      workspaceId,
      query: { branch: options.branch },
    }).catch(() => undefined)
    fileMimeType = remote?.mimeType
  }

  return buildLocalContext({
    apiUrl: api.apiUrl,
    accessToken: token,
    scopes,
    workspaceId,
    filePath: options.file,
    fileMimeType,
    branch: options.branch,
    tool: options.tool,
    mode: options.mode,
    timeoutSeconds: options.timeout ? Number(options.timeout) : undefined,
    args: options.arg,
    env,
    parameters: options.params ? (JSON.parse(options.params) as Record<string, unknown>) : undefined,
  })
}

export function registerDev(program: Command): void {
  const devCmd = program
    .command('dev')
    .description('Local development loop for engine/app scripts (real workspace access from your machine)')

  devCmd
    .command('token')
    .description('Mint a workspace-bound access token like a platform run receives')
    .option('--workspace <workspaceId>')
    .option('--scopes <scope...>', 'explicit scopes (default: manifest spec.permissions, else the engine default set)')
    .option('--manifest <path>', 'engine/app manifest to read spec.permissions from')
    .option('--boundary <path>', 'confine the token to this workspace directory subtree (like platform runs)')
    .option('--branch <branch>', 'workspace branch', 'main')
    .option('--json', 'output JSON with token metadata')
    .action(async (options: TokenFlags) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      const scopes = await resolveDevScopes(options)
      const token = await mintDevToken(api, workspaceId, scopes, options.boundary, options.branch)
      if (options.json) {
        const payload = decodeJwtPayload(token)
        printJson({
          access_token: token,
          scopes,
          workspaceId,
          expiresAt: payload?.exp ? new Date(payload.exp * 1000).toISOString() : undefined,
        })
        return
      }
      console.log(token)
    })

  devCmd
    .command('context')
    .description("Write a real context.json (the platform's engine contract) for local runs")
    .option('--workspace <workspaceId>')
    .option('--file <path>', 'workspace file path the run targets (engine.context.file)')
    .option('--tool <tool>', 'engine tool name (e.g. execute)', 'execute')
    .option('--mode <mode>', 'execution mode', 'request-response')
    .option('--timeout <seconds>', 'execution timeout')
    .option('--arg <value...>', 'command-line arguments (repeatable)')
    .option('--env <KEY=value...>', 'environment entries for engine.context.input.environment (repeatable)')
    .option('--params <json>', 'tool parameters JSON')
    .option('--scopes <scope...>', 'explicit token scopes')
    .option('--manifest <path>', 'manifest to read spec.permissions from')
    .option('--boundary <path>', 'confine the token to this directory subtree')
    .option('--branch <branch>', 'workspace branch', 'main')
    .option('--out <path>', 'output path', '.verentis/context.json')
    .action(async (options: ContextFlags) => {
      const api = await createApiClient()
      const context = await buildContextFromFlags(api, options)
      const outPath = resolve(options.out ?? '.verentis/context.json')
      await mkdir(dirname(outPath), { recursive: true })
      await writeFile(outPath, JSON.stringify(context, null, 2) + '\n', { mode: 0o600 })
      console.log(`Wrote ${outPath} (token expires ${context.platform.token.expiresAt ?? 'unknown'}).`)
      console.log('\nRun your script with the SDK pointed at it:')
      console.log(`  export VERENTIS_CONTEXT_PATH=${outPath}`)
      console.log('  python your_script.py')
    })

  devCmd
    .command('run <script>')
    .description('Run a local script against the REAL workspace (mint token → context.json → spawn)')
    .option('--workspace <workspaceId>')
    .option('--file <path>', 'workspace file path the run pretends to target (default: /<script name>)')
    .option('--tool <tool>', 'engine tool name', 'execute')
    .option('--mode <mode>', 'execution mode', 'request-response')
    .option('--timeout <seconds>', 'execution timeout')
    .option('--arg <value...>', 'arguments passed to the script (repeatable)')
    .option('--env <KEY=value...>', 'extra environment variables (repeatable)')
    .option('--params <json>', 'tool parameters JSON')
    .option('--scopes <scope...>', 'explicit token scopes')
    .option('--manifest <path>', 'manifest to read spec.permissions from')
    .option('--boundary <path>', 'confine the token to this directory subtree')
    .option('--branch <branch>', 'workspace branch', 'main')
    .option('--python <interpreter>', 'python interpreter', 'python3')
    .option('--debug [port]', 'wait for a debugger: wraps with debugpy --listen <port> --wait-for-client')
    .option('--keep-context', 'keep the generated .verentis/context.json after the run')
    .action(async (script: string, options: ContextFlags & { python: string; debug?: string | boolean; keepContext?: boolean }) => {
      const api = await createApiClient()
      const scriptPath = resolve(script)
      const effective = { ...options, file: options.file ?? `/${script.replace(/^\.\//, '')}` }
      const context = await buildContextFromFlags(api, effective)

      const contextPath = resolve('.verentis/context.json')
      await mkdir(dirname(contextPath), { recursive: true })
      await writeFile(contextPath, JSON.stringify(context, null, 2) + '\n', { mode: 0o600 })

      const env = {
        ...process.env,
        ...context.input.environment,
        ...contextEnv(context, contextPath),
      }

      const pythonArgs: string[] = []
      if (options.debug !== undefined) {
        const port = typeof options.debug === 'string' ? options.debug : '5678'
        pythonArgs.push('-m', 'debugpy', '--listen', port, '--wait-for-client')
        console.error(`Waiting for a debugger to attach on port ${port} (VS Code: "Python Debugger: Attach")…`)
      }
      pythonArgs.push(scriptPath, ...(context.input.arguments ?? []))

      console.error(`→ ${options.python} ${pythonArgs.join(' ')}`)
      console.error(`  workspace ${context.workspace.id} · branch ${options.branch} · token expires ${context.platform.token.expiresAt ?? 'unknown'}\n`)

      const child = spawn(options.python, pythonArgs, { env, stdio: 'inherit' })
      const exitCode: number = await new Promise((resolveExit) => {
        child.on('close', (code) => resolveExit(code ?? 1))
        child.on('error', (error) => {
          console.error(`Failed to start ${options.python}: ${error.message}`)
          if (options.debug !== undefined) console.error('Is debugpy installed? pip install debugpy')
          resolveExit(1)
        })
      })
      process.exitCode = exitCode
    })

  devCmd
    .command('vscode')
    .description('Scaffold .vscode/launch.json with attach + launch configs for the dev loop')
    .option('--port <port>', 'debugpy port', '5678')
    .action(async (options: { port: string }) => {
      const launch = {
        version: '0.2.0',
        configurations: [
          {
            name: 'Verentis: attach to dev run',
            type: 'debugpy',
            request: 'attach',
            connect: { host: 'localhost', port: Number(options.port) },
            justMyCode: true,
          },
          {
            name: 'Verentis: launch current file with context',
            type: 'debugpy',
            request: 'launch',
            program: '${file}',
            console: 'integratedTerminal',
            justMyCode: true,
            env: { VERENTIS_CONTEXT_PATH: '${workspaceFolder}/.verentis/context.json' },
          },
        ],
      }
      const outPath = resolve('.vscode/launch.json')
      await mkdir(dirname(outPath), { recursive: true })
      await writeFile(outPath, JSON.stringify(launch, null, 2) + '\n')
      console.log(`Wrote ${outPath}.`)
      console.log('Workflow:')
      console.log('  1. verentis dev run script.py --debug        # waits for the debugger')
      console.log('  2. F5 → "Verentis: attach to dev run"        # breakpoints hit your local code')
      console.log('  or: verentis dev context && F5 → "launch current file with context"')
    })
}
